/**
 * The Skin row's field: the asset picker, offering only the skin set's
 * models, by skin name and thumbnail. Choosing one runs `skinEdit`, an
 * ordinary edit of the asset prop that keeps the playing clip where it can.
 */
import type { SkinSet } from "../../assets/content";
import type { EntityRecord } from "../../world/entity";
import { runEdit, useEditor } from "../EditorContext";
import { AssetPickerField } from "../widgets/AssetPicker";
import type { AssetOption } from "../widgets/WidgetEnvironment";
import { skinEdit } from "./skinEdit";

export interface SkinFieldProps {
  readonly id: string;
  readonly entities: readonly EntityRecord[];
  readonly component: string;
  /** The asset prop the skins are models for. */
  readonly propKey: string;
  readonly set: SkinSet;
}

export function SkinField({ id, entities, component, propKey, set }: SkinFieldProps) {
  const services = useEditor();
  const { content, components } = services;
  // The row only exists while every entity holds the same model (inspectorModel).
  const current = entities[0]?.components.get(component)?.[propKey];
  const options = set.skins.map((skin): AssetOption => {
    const thumbnail = content.index.assets.find((asset) => asset.path === skin.model)?.thumbnail;
    return {
      value: skin.model,
      label: skin.name,
      detail: "",
      ...(thumbnail !== undefined && { thumbnail: content.url(thumbnail) }),
    };
  });
  const shown = options.find((option) => option.value === current) ?? options[0];
  if (!shown || typeof current !== "string") return null;

  const choose = (model: string | null) => {
    const skin = set.skins.find((candidate) => candidate.model === model);
    if (!skin || skin.model === current) return;
    runEdit(
      services,
      skinEdit({
        entities,
        target: { component, prop: propKey },
        skin,
        components,
        clipsOf: (path) => content.index.assets.find((asset) => asset.path === path)?.clips ?? [],
        origin: "inspector",
      }),
    );
  };

  return (
    <AssetPickerField
      id={id}
      options={options}
      nullable={false}
      current={current}
      shown={shown}
      onPick={choose}
    />
  );
}
